import { useEffect, useMemo, useRef, useState } from "react";

/**
 * CustomCodeSection — renders user-provided HTML / CSS / JS inside a sandboxed
 * iframe so authors can drop in any custom markup (embeds, calculators,
 * animations, forms) without breaking the parent page. Height auto-fits by
 * default (no cap), or the author can pin an exact height.
 *
 * Because the iframe is same-origin (allow-same-origin) we read
 * `iframe.contentDocument.body.scrollHeight` directly instead of relying on
 * postMessage, which turned out to race with React's useEffect on load.
 */
export default function CustomCodeSection({ section }) {
  const s = section || {};
  const ref = useRef(null);
  const [autoHeight, setAutoHeight] = useState(null);

  const srcDoc = useMemo(() => {
    const html = s.html || "";
    const css = s.css || "";
    const js = s.js || "";
    const headExtras = s.head_extras || ""; // preserved <link>, <meta>, <title> from paste
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
${headExtras}
<style>
  html, body { margin:0; padding:0; }
  body { font-family: 'Manrope', -apple-system, Segoe UI, Roboto, sans-serif; color:#0B0D17; }
  ${css}
</style>
</head>
<body>
${html}
<script>
  (function(){
    try {
      ${js}
    } catch(e) { console.error('[custom_code]', e); }
  })();
</script>
</body>
</html>`;
  }, [s.html, s.css, s.js, s.head_extras]);

  // Same-origin height sync — read directly, no postMessage race.
  // We track the MAX observed content height per srcDoc so the iframe never
  // shrinks under its content (many templates use `min-height:100vh` which
  // makes body scrollHeight collapse once the iframe first grows).
  useEffect(() => {
    let cancelled = false;
    let maxSeen = 0;
    let ro = null;
    let mo = null;
    let pollTimer = null;
    const measure = () => {
      if (cancelled) return;
      try {
        const el = ref.current;
        if (!el) return;
        const doc = el.contentDocument;
        if (!doc || !doc.body) return;
        const bodyH = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        let farthest = 0;
        try {
          const kids = doc.body.children;
          for (let i = 0; i < kids.length; i++) {
            const r = kids[i].getBoundingClientRect();
            const bottom = r.top + r.height + (doc.documentElement.scrollTop || 0);
            if (bottom > farthest) farthest = bottom;
          }
        } catch (_e) { /* ignore */ }
        const h = Math.max(bodyH, farthest);
        if (h > 0 && h > maxSeen) {
          maxSeen = h;
          setAutoHeight(h);
        }
      } catch (_e) { /* cross-origin — ignore */ }
    };
    const attachInside = () => {
      try {
        const el = ref.current;
        if (!el || !el.contentDocument) return;
        const win = el.contentWindow;
        const doc = el.contentDocument;
        if (typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver(measure);
          ro.observe(doc.body);
          ro.observe(doc.documentElement);
        }
        mo = new MutationObserver(measure);
        mo.observe(doc.body, { childList: true, subtree: true, attributes: true });
        win.addEventListener("load", measure);
        win.addEventListener("resize", measure);
        if (doc.fonts && doc.fonts.ready && doc.fonts.ready.then) doc.fonts.ready.then(measure).catch(() => {});
        // Poll several times to catch async images / lazy content
        [50, 200, 600, 1500, 3000, 6000].forEach((ms) => setTimeout(measure, ms));
      } catch (_e) { /* ignore */ }
    };
    const onLoad = () => { measure(); attachInside(); };
    const el = ref.current;
    if (el) {
      el.addEventListener("load", onLoad);
      setTimeout(measure, 30);
    }
    pollTimer = setInterval(measure, 4000);
    return () => {
      cancelled = true;
      if (el) el.removeEventListener("load", onLoad);
      if (ro) { try { ro.disconnect(); } catch (_e) {} }
      if (mo) { try { mo.disconnect(); } catch (_e) {} }
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [srcDoc]);

  const pinnedH = Number(s.height) > 0 ? Number(s.height) : null;
  const height = pinnedH || autoHeight || 600;

  return (
    <section className="w-full">
      <iframe
        ref={ref}
        title={`custom-code-${s.id || "section"}`}
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
        style={{ width: "100%", height: `${height}px`, border: 0, display: "block" }}
      />
    </section>
  );
}
